import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../Global/Layout';
import Swal from 'sweetalert2';
import { API_CONFIG } from '../../../contexts/constants';
import { formatDateTimeDefault } from '../../../utils/dateFormatter';

const Detail = ({ onLogout, userRole }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [newsItem, setNewsItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNewsDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  	if (!['sysadmin', 'owner', 'admin', 'agent', 'customer'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

  const fetchNewsDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_CONFIG.BASE_URL}/news/${id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success) {
        setNewsItem(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching news detail:', error);

      // แสดง error message จาก API
      let errorMessage = 'ไม่สามารถโหลดรายละเอียดข่าวได้';

      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: errorMessage,
        confirmButtonText: 'ตกลง'
      }).then(() => {
        navigate(-1); // กลับไปหน้าก่อนหน้า
      });
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Layout title="รายละเอียดข่าวสาร" onLogout={onLogout} userRole={userRole}>
        <div className="text-center py-12 text-gray-500 dark:text-gray-300">กำลังโหลดข้อมูล...</div>
      </Layout>
    );
  }

  if (!newsItem) {
    return (
      <Layout title="ไม่พบข่าวสาร" onLogout={onLogout} userRole={userRole}>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">ไม่พบข่าวสารที่ค้นหา</h2>
          <button
            onClick={goBack}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
          >
            กลับหน้าหลัก
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title={newsItem ? newsItem.title : "รายละเอียดข่าวสาร"}
      onLogout={onLogout}
      userRole={userRole}
      breadcrumbItems={newsItem ? [
        { label: 'ข่าวสารและโปรโมชั่น', path: '/news/views', icon: 'news' },
        { label: 'ข่าวสารและโปรโมชั่น', path: '/news/views', icon: 'news' },
        { label: newsItem.title, path: `/news/views/${newsItem.id}`, icon: null }
      ] : null}
    >
      <div className="space-y-6">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={goBack}
            className="flex items-center text-blue-600 dark:text-blue-400 hover:underline"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            กลับหน้าก่อนหน้า
          </button>
        </div>

        {/* Main Content */}
        <article className="bg-white dark:bg-surface-dark rounded-xl shadow-card dark:shadow-carddark overflow-hidden">
          {/* Article Header */}
          <div className={`p-6 border-l-4 ${
            newsItem.priority === 'high'
              ? 'border-l-red-500'
              : newsItem.priority === 'medium'
              ? 'border-l-yellow-500'
              : 'border-l-blue-500'
          }`}>
            <div className="flex items-start justify-between mb-4">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  newsItem.type === 'promotion'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                }`}
              >
                {newsItem.badge}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{formatDateTimeDefault(newsItem.date)}</span>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {newsItem.title}
            </h1>

            <div className="flex items-center text-gray-600 dark:text-gray-400">
              {newsItem.type === 'promotion' ? (
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              )}
              <span>{newsItem.type === 'promotion' ? 'โปรโมชั่น' : 'ข่าวสาร'}</span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6">
            {/* Short Detail */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">สรุปข่าว</h2>
              <div
                className="text-gray-700 dark:text-gray-300 leading-relaxed prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: newsItem.content }}
              />
            </div>

            {/* Full Detail */}
			<div className="border-t border-gray-200 dark:border-gray-700 pt-6">
			<h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">รายละเอียดเต็ม</h2>

			<div className="space-y-4">
				<div>
					<h3 className="font-semibold text-gray-900 dark:text-white mb-2">เงื่อนไขและข้อกำหนด:</h3>
					<ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
						{newsItem.fullDetail?.conditions?.map((condition, index) => (
							condition ? <li key={index}>{condition}</li> : null
						)) || []}
					</ul>
				</div>

				<div>
					<h3 className="font-semibold text-gray-900 dark:text-white mb-2">วิธีการเข้าร่วม:</h3>
					<ol className="list-decimal list-inside space-y-1 text-gray-700 dark:text-gray-300">
						{newsItem.fullDetail?.howToJoin?.map((step, index) => (
							step ? <li key={index}>{step}</li> : null
						)) || []}
					</ol>
				</div>

				{newsItem.type === 'promotion' && (
					newsItem.fullDetail?.rewards &&
					<div>
						<h3 className="font-semibold text-gray-900 dark:text-white mb-2">รางวัลและผลตอบแทน:</h3>
						<div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
							<p className="text-green-800 dark:text-green-400">
								{newsItem.fullDetail?.rewards || ''}
							</p>
						</div>
					</div>
				)}
			</div>
			</div>
          </div>
        </article>
      </div>
    </Layout>
  );
};

export default Detail;
